import { Play } from "lucide-react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import type { MatchRecap, RecapKeyPlay } from "@/types"

import { CommentaryDesk } from "./commentary-desk"
import { MiniEventLog } from "./mini-event-log"
import { MiniReplayCard } from "./mini-replay-card"

interface KeyPlayExpandedOverlayProps {
  mode: "image" | "replay" | "commentary"
  onCollapse: () => void
  play: RecapKeyPlay
  recap: MatchRecap
  hasReplay: boolean
  homeTeamId: string
  homeTeamHue: number
  awayTeamHue: number
}

/**
 * Full-screen modal for a key play — image zoom, interactive mini-replay with
 * play/pause/restart controls and a turn-by-turn event log, or a scrollable
 * commentary reader. Owns the mini-replay control state; opened and closed by
 * the parent beat via `mode` / `onCollapse`.
 */
export function KeyPlayExpandedOverlay({
  mode,
  onCollapse,
  play,
  recap,
  hasReplay,
  homeTeamId,
  homeTeamHue,
  awayTeamHue,
}: KeyPlayExpandedOverlayProps) {
  const [paused, setPaused] = useState(false)
  const [replayKey, setReplayKey] = useState(0)
  const [currentReplayEventIndex, setCurrentReplayEventIndex] = useState(-1)

  const commentatorMap = Object.fromEntries(
    recap.commentators.map((c) => [c.key, c]),
  )

  const handlePlayPause = (e: React.MouseEvent) => {
    e.stopPropagation()
    setPaused((p) => !p)
  }

  const handleReplay = (e: React.MouseEvent) => {
    e.stopPropagation()
    setReplayKey((k) => k + 1)
    setPaused(false)
  }

  return (
    <div
      className="absolute inset-0 flex items-center justify-center"
      style={{ zIndex: 30 }}
      role="button"
      tabIndex={0}
      onClick={onCollapse}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          onCollapse()
        }
      }}
    >
      {/* Dimmed background */}
      <div
        className="absolute inset-0"
        style={{ background: "oklch(0 0 0 / 0.85)" }}
      />

      {/* Expanded content */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div
        className="relative"
        style={{
          width: "90%",
          maxWidth: 900,
          height: "80%",
          zIndex: 31,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {mode === "image" && play.image_url && (
          <img
            src={play.image_url}
            alt={play.title}
            className="h-full w-full rounded-lg object-contain"
          />
        )}
        {mode === "replay" && hasReplay && (
          <div className="flex h-full w-full flex-col overflow-hidden">
            {/* Pitch visualization */}
            <div className="relative min-h-0 flex-1">
              <MiniReplayCard
                key={`expanded-${replayKey}`}
                replaySnapshot={play.replay_snapshot!}
                replayEvents={play.replay_events!}
                playersData={recap.players_data!}
                homeTeamId={homeTeamId}
                homeTeamHue={homeTeamHue}
                awayTeamHue={awayTeamHue}
                paused={paused}
                onEventChange={setCurrentReplayEventIndex}
              />
              {/* Replay controls — inside pitch area */}
              {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
              <div
                className="absolute bottom-3 left-1/2 z-20 flex -translate-x-1/2 items-center gap-3"
                onClick={(e) => e.stopPropagation()}
              >
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleReplay}
                  className="flex h-9 w-9 items-center justify-center transition-colors hover:brightness-125"
                  style={{
                    background: "oklch(0.18 0.03 55 / 0.85)",
                    backdropFilter: "blur(8px)",
                    color: "oklch(0.65 0.08 55)",
                    boxShadow: "0 2px 12px oklch(0 0 0 / 0.5)",
                  }}
                  title="Replay"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="size-4"
                  >
                    <path d="M3 12a9 9 0 1 1 9 9" />
                    <polyline points="1 17 3 21 7 19" />
                  </svg>
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handlePlayPause}
                  className="flex h-11 w-11 items-center justify-center transition-colors hover:brightness-125"
                  style={{
                    background: "oklch(0.25 0.05 55 / 0.85)",
                    backdropFilter: "blur(8px)",
                    color: "var(--accent)",
                    boxShadow:
                      "0 0 14px oklch(0.78 0.18 55 / 0.2), 0 2px 12px oklch(0 0 0 / 0.5)",
                  }}
                  title={paused ? "Play" : "Pause"}
                >
                  {paused ? (
                    <Play className="size-5" fill="currentColor" />
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="size-5"
                    >
                      <rect x="6" y="4" width="4" height="16" rx="1" />
                      <rect x="14" y="4" width="4" height="16" rx="1" />
                    </svg>
                  )}
                </Button>
              </div>
            </div>
            {/* Turn-by-turn event log */}
            <div className="flex-none">
              <MiniEventLog
                events={play.replay_events!}
                currentEventIndex={currentReplayEventIndex}
                homeTeamId={homeTeamId}
                homeTeamHue={homeTeamHue}
                awayTeamHue={awayTeamHue}
              />
            </div>
          </div>
        )}
        {mode === "commentary" && (
          <div
            className="h-full w-full overflow-y-auto p-6"
            style={{
              background: "oklch(0.08 0.02 55 / 0.95)",
              backdropFilter: "blur(16px)",
              WebkitBackdropFilter: "blur(16px)",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            <div className="mx-auto max-w-2xl">
              <CommentaryDesk
                commentary={play.commentary}
                commentatorMap={commentatorMap}
                portraitUrls={recap.portrait_urls}
                opacities={play.commentary.map(() => 1)}
                translateXs={play.commentary.map(() => 0)}
                translateYs={play.commentary.map(() => 0)}
                rotates={play.commentary.map(() => 0)}
                scales={play.commentary.map(() => 1)}
              />
            </div>
          </div>
        )}

        {/* Close button */}
        <Button
          type="button"
          variant="ghost"
          onClick={onCollapse}
          className="absolute top-3 right-3 flex h-9 w-9 items-center justify-center"
          style={{
            background: "oklch(0.12 0.02 55 / 0.85)",
            backdropFilter: "blur(8px)",
            color: "oklch(0.65 0.08 55)",
            boxShadow: "0 2px 8px oklch(0 0 0 / 0.5)",
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="size-4"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </Button>
      </div>
    </div>
  )
}
